export interface PricingPreviewFeature {
  title: string;
  detail: string;
}

export interface PricingPreview {
  description: string;
  outcome: string;
  monthlyCredits: string;
  creditsDetail: string;
  features: PricingPreviewFeature[];
}

// Shown when a plan has no entry below (fallback cards built from config.ts).
export const DEFAULT_CREDITS_DETAIL = '1 processed page = 1 credit.'

// Shared positioning preview for the upgrade modal and onboarding offer.
// Canonical prices and enforced entitlements remain in config.ts.
export const pricingPreview: Record<string, PricingPreview> = {
  starter: {
    description: 'For freelancers',
    outcome: 'Everything you need to capture, extract and export your documents.',
    monthlyCredits: '100',
    creditsDetail: 'Covers your monthly receipts and invoices — 1 processed page = 1 credit.',
    features: [
      {
        title: '1 workspace',
        detail: 'All your documents organised in one set of books.',
      },
      {
        title: 'Forward receipts by email',
        detail: 'Email your receipts to receipts@receiptsai.com and they land in your books.',
      },
      {
        title: 'Automatic receipt processing',
        detail: 'Filter, extract and organise new receipts with less manual work.',
      },
      {
        title: 'Automatic currency conversion',
        detail: 'Foreign receipts converted to your currency at the latest exchange rates.',
      },
      {
        title: 'Reports',
        detail: 'Generate expense reports, monthly summaries and spending charts.',
      },
      {
        title: 'Excel-ready exports',
        detail: 'Turn receipts and PDFs into structured spreadsheet data.',
      },
      {
        title: '30 categories and custom rules',
        detail: 'Organise extracted data before you export it.',
      },
      {
        title: 'Privacy controls',
        detail: 'Hide sensitive words and phrases before your documents are processed by AI.',
      },
    ],
  },
  premium: {
    description: 'For small businesses',
    outcome: 'Business and personal books, kept separate and organised automatically.',
    monthlyCredits: '500',
    creditsDetail: 'Room for a full month of business paperwork across both workspaces.',
    features: [
      {
        title: '2 workspaces',
        detail: 'One for your business, one for your personal life — completely separate.',
      },
      {
        title: '2 connected mailboxes',
        detail: 'Set up a Gmail or Outlook forwarding rule once — we confirm it and receipts flow in automatically.',
      },
      {
        title: 'Mailbox-to-workspace routing',
        detail: 'Each connected mailbox forwards to one workspace’s address, so business and personal never mix.',
      },
      {
        title: 'Receipt matching',
        detail: 'Automatically match bank transactions with their receipts.',
      },
      {
        title: 'Historical currency conversion',
        detail: 'Converts at the exchange rate from each document’s date, for accurate books.',
      },
      {
        title: '100 categories and custom rules',
        detail: 'Automatically organise extracted data across both workspaces.',
      },
    ],
  },
  'premium-plus': {
    description: 'For small businesses with employees',
    outcome: 'Your whole team feeds the books, and you stay in control.',
    monthlyCredits: '1,500',
    creditsDetail: 'Headroom for receipts flowing in from your whole team.',
    features: [
      {
        title: 'Up to 5 workspaces',
        detail: 'Room for more businesses or projects, each with its own books.',
      },
      {
        title: 'Up to 5 connected mailboxes',
        detail: 'Give each part of the business its own automated source of receipts.',
      },
      {
        title: 'Up to 2 users',
        detail: 'Invite your bookkeeper or business partner to view and edit everything in the app, just like you.',
      },
      {
        title: 'Up to 5 senders',
        detail: 'Employees email receipts and invoices in — nothing to log into, they never see the app.',
      },
    ],
  },
  pro: {
    description: 'For bookkeepers and accountants managing clients',
    outcome: 'Run every client from one dashboard.',
    monthlyCredits: '3,000',
    creditsDetail: 'Enough volume to run every client’s books side by side.',
    features: [
      {
        title: '10 client workspaces',
        detail: 'Keep every client’s documents, rules and reports separate.',
      },
      {
        title: 'Clients dashboard',
        detail: 'See activity and move between client books from one place.',
      },
      {
        title: '5 connected client mailboxes',
        detail: 'Automate receipt intake across your busiest accounts.',
      },
      {
        title: 'Up to 5 editing teammates',
        detail: 'Give your bookkeeping team shared access with clear roles.',
      },
      {
        title: 'Unlimited custom rules',
        detail: 'Standardise document organisation across every client.',
      },
    ],
  },
  'pro-plus': {
    description: 'For high-volume firms that need maximum capacity',
    outcome: 'Maximum capacity with fewer operational limits.',
    monthlyCredits: '10,000',
    creditsDetail: 'Our highest capacity, built for high-volume client intake.',
    features: [
      {
        title: '25 client workspaces',
        detail: 'Manage a larger client portfolio from one account.',
      },
      {
        title: '10 connected mailboxes',
        detail: 'Keep high-volume client intake running automatically.',
      },
      {
        title: 'Founder-level support',
        detail: 'Get direct help for setup and high-volume operations.',
      },
    ],
  },
}
